import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { IRootState } from '../../../../store/reducers';
import warnImage from '../../../../assets/images/warn-yellow.png';
import { ModalWrap } from '../../../component/modal-wrap';
import { IEnvService, IGameEnvModel } from '../../../../model/api-modules/env';
import {
  IAPIGameItem,
  IAPIServiceConfigItem,
  IAuditItem,
} from '../../../../model/api-modules';
import {
  getEnvCollectionList,
  GamesAction,
} from '../../../../store/modules/games';
import { Dispatch } from 'redux';
import { serviceOpenList } from '../../../../api/services';
import { getEnvNameByValue } from '../../../utils/env';
import { removeEnv } from '../../../../api/game/env';
import { messageError, messageSuccess } from '../../../../components/message';
import getFormatMessage from '../../../../locales/get-format-message';
import { auditWriteLog } from '../../../lib/aduit-log';

interface IProps {
  readonly selectRow: IGameEnvModel | null;
  readonly selectedGame?: IAPIGameItem;
  readonly studioId: string | null;
  readonly refresh: (isDeletedEnv: boolean) => void;
  readonly onClose: () => void;
  readonly allServiceConfig: ReadonlyArray<IAPIServiceConfigItem> | null;
}

export const DeleteEnvModal = (props: IProps) => {
  const games = useSelector((state: IRootState) => state.games.games);
  const gamesDispatch = useDispatch<Dispatch<GamesAction>>();
  const [loading, setLoading] = useState<boolean>(false);
  const [hasService, setHasService] = useState<boolean>(false);

  const deleteEnv = async () => {
    setLoading(true);
    const { selectRow, studioId, selectedGame } = props;
    const studioName = games.find(
      (item) => item.studioID === props.studioId
    )?.studioName;
    try {
      if (selectRow && studioId && selectedGame && studioName) {
        const servicesDelete = await serviceOpenList(
          selectedGame.gameID,
          selectRow.id
        );
        const servicesIds = servicesDelete.map((item) => item.ServiceID);
        const serviceNames = servicesIds.map((id) => {
          const menuConfigItem = props?.allServiceConfig?.find(
            (item) => item.id === id
          );
          return menuConfigItem ? menuConfigItem.Name : '';
        });
        const regionName = getEnvNameByValue(selectRow.Region);
        const body: IEnvService = {
          gameID: selectRow.GameID,
          gameName: selectedGame.gameName,
          studioID: studioId,
          studioName,
          regionName,
          envs: [
            {
              envName: selectRow.Name,
              envType: selectRow.Type,
              serviceNames,
              region: selectRow.Region,
              envID: selectRow.Guid,
            },
          ],
        };
        const res = await removeEnv(selectRow.id, body);
        auditWriteLog(IAuditItem.DeleteGameEnvironment, {
          gameID: body.gameID,
          envID: selectRow.Guid,
          envType: selectRow.Type,
          detail: {
            ...body,
            envID: selectRow.Guid,
          },
        });
        if (res?.code === 0) {
          await getEnvCollectionList(gamesDispatch)(studioId);
          messageSuccess(getFormatMessage('env_del_success'));
          // 更新环境列表
          props.refresh(true);
        } else if (res?.code === 101921) {
          // 要删除游戏的环境不存在
          messageError(getFormatMessage('env_not_exist'));
        } else if (res) {
          messageError(getFormatMessage('env_del_fail'));
        }
      }
    } catch (error: any) {
      messageError(
        error?.apiError?.message || getFormatMessage('env_del_fail')
      );
    }
    props.onClose();
    setLoading(false);
    return true;
  };

  const getServices = async (row: IGameEnvModel) => {
    const { GameID: gameId, id } = row;
    const serviceList = await serviceOpenList(gameId, id);
    setHasService(!!serviceList.length);
  };

  useEffect(() => {
    if (props.selectRow) {
      getServices(props.selectRow);
    }
  }, [props.selectRow]);

  const deleteText = useMemo(() => {
    const name = props.selectRow?.Name;
    return hasService
      ? getFormatMessage('migrate_game_confirm_has_service_desc', {
          params: {
            name,
          },
        })
      : getFormatMessage('migrate_game_confirm_desc', {
          params: {
            name,
          },
        });
  }, [hasService, props.selectRow]);

  return (
    <div>
      {/* 未曾开通服务 */}
      <ModalWrap
        width="440px"
        loading={loading}
        onOk={deleteEnv}
        cancelText={getFormatMessage('cancel')}
        oKText={getFormatMessage('confirm')}
        onClose={props.onClose}
        title={
          <p className="fail-title">
            <img
              src={warnImage}
              alt="Warning"
              style={{ marginRight: '10px', marginTop: '-2px' }}
            />
            <span>{getFormatMessage('del_environment')}</span>
          </p>
        }
        className="delete-env-modal delete-modal"
      >
        <div className="remove-text" style={{ whiteSpace: 'pre-line' }}>
          {deleteText}
        </div>
      </ModalWrap>
    </div>
  );
};
